import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { AnalysisResult, Trial } from '../types';

interface VelocityLossChartProps {
  result: AnalysisResult;
  vFree: number;
  trials: Trial[];
}

export const VelocityLossChart: React.FC<VelocityLossChartProps> = ({ result, vFree, trials }) => {
  const data = trials.map(trial => ({
    load: trial.load_kgf,
    velocityLoss: ((vFree - trial.v_max_ms) / vFree) * 100,
    velocity: trial.v_max_ms
  }));

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Velocity Loss Profile</h3>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="load" 
              stroke="#9CA3AF"
              label={{ value: 'Load (kg)', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <YAxis 
              stroke="#9CA3AF"
              label={{ value: 'Velocity Loss (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151', 
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
              formatter={(value: number, name: string) => [
                name === 'velocityLoss' ? `${value.toFixed(1)}%` : `${value.toFixed(1)} m/s`,
                name === 'velocityLoss' ? 'Velocity Loss' : 'Velocity'
              ]}
              labelFormatter={(label) => `Load: ${label} kg`}
            />
            <Line 
              type="monotone" 
              dataKey="velocityLoss" 
              stroke="#EF4444" 
              strokeWidth={3}
              dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
            />
            <ReferenceLine 
              y={10} 
              stroke="#10B981" 
              strokeDasharray="5 5"
              label={{ value: "10% Threshold", position: "right", style: { fill: '#10B981' } }}
            />
            <ReferenceLine 
              y={25} 
              stroke="#F59E0B" 
              strokeDasharray="5 5"
              label={{ value: "25% Threshold", position: "right", style: { fill: '#F59E0B' } }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-theme-muted">
        <p>Load Tolerance: <span className="text-green-400">Excellent</span> (&lt;10%), <span className="text-yellow-400">Good</span> (10-25%), <span className="text-red-400">Poor</span> (&gt;25%)</p>
      </div>
    </div>
  );
};